// ==================== صفحة المستخدمين المحذوفين ====================
// للمدير فقط - يمكن استعادة أو حذف المستخدمين نهائياً

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Trash2, 
  RotateCcw, 
  User, 
  AlertTriangle,
  Calendar,
  Search,
  Filter
} from 'lucide-react';
import api from '../../utils/api';

const DeletedUsers = () => {
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);

  useEffect(() => {
    fetchDeletedUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDeletedUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/barbers/deleted/all');
      setDeletedUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching deleted users:', error);
      toast.error('خطأ في جلب المستخدمين المحذوفين');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (userId) => {
    try {
      await api.post(`/barbers/deleted/${userId}/restore`);
      toast.success('تم استعادة المستخدم بنجاح');
      setConfirmRestore(null);
      fetchDeletedUsers();
    } catch (error) {
      console.error('Error restoring user:', error);
      toast.error('خطأ في استعادة المستخدم');
    }
  };

  const handlePermanentDelete = async (userId) => {
    try {
      await api.delete(`/barbers/deleted/${userId}/permanent`);
      toast.success('تم حذف المستخدم نهائياً');
      setConfirmDelete(null);
      fetchDeletedUsers();
    } catch (error) {
      console.error('Error permanently deleting user:', error);
      toast.error('خطأ في حذف المستخدم');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'غير محدد';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredUsers = deletedUsers.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0d0d0d] to-[#1a1a1a] p-4">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#d4af37]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0d0d0d] to-[#1a1a1a] p-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* العنوان */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#d4af37] mb-2">
            المستخدمين المحذوفين
          </h1>
          <p className="text-gray-400 text-sm">
            يمكنك استعادة المستخدمين أو حذفهم نهائياً من هنا
          </p>
        </div>

        {/* شريط البحث */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="بحث بالاسم أو رقم الهاتف..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg py-3 pr-12 pl-4 text-white placeholder-gray-500 focus:border-[#d4af37] focus:outline-none"
              data-testid="search-deleted-users"
            />
          </div>
        </div>

        {/* إحصائيات */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">إجمالي المحذوفين</p>
                  <p className="text-2xl font-bold text-[#d4af37]">{deletedUsers.length}</p>
                </div>
                <Trash2 className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">الحلاقين المحذوفين</p>
                  <p className="text-2xl font-bold text-white">
                    {deletedUsers.filter(u => u.original_type === 'barber').length}
                  </p>
                </div>
                <User className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">قابل للاستعادة</p>
                  <p className="text-2xl font-bold text-green-500">{deletedUsers.length}</p>
                </div>
                <RotateCcw className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* قائمة المحذوفين */}
        {filteredUsers.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-8 text-center">
              <Trash2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">لا يوجد مستخدمين محذوفين</p>
              <p className="text-gray-500 text-sm mt-2">
                عند حذف أي حلاق سيظهر هنا ويمكن استعادته أو حذفه نهائياً
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredUsers.map((user) => (
              <Card 
                key={user.id} 
                className="bg-[#1a1a1a] border-[#333] hover:border-[#d4af37]/30 transition-all"
                data-testid={`deleted-user-${user.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    {/* معلومات المستخدم */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{user.name}</h3>
                        <p className="text-gray-400 text-sm">{user.phone || 'لا يوجد رقم'}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>تم الحذف: {formatDate(user.deleted_at)}</span>
                          {user.deleted_by_name && (
                            <span className="mr-2">بواسطة: {user.deleted_by_name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* أزرار الإجراءات */}
                    <div className="flex items-center gap-2">
                      {/* زر الاستعادة */}
                      {confirmRestore === user.id ? (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleRestore(user.id)}
                            data-testid={`confirm-restore-${user.id}`}
                          >
                            تأكيد الاستعادة
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-gray-600 text-gray-400"
                            onClick={() => setConfirmRestore(null)}
                          >
                            إلغاء
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-600 text-green-500 hover:bg-green-600/20"
                          onClick={() => setConfirmRestore(user.id)}
                          data-testid={`restore-btn-${user.id}`}
                        >
                          <RotateCcw className="w-4 h-4 ml-1" />
                          استعادة
                        </Button>
                      )}

                      {/* زر الحذف النهائي */}
                      {confirmDelete === user.id ? (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => handlePermanentDelete(user.id)}
                            data-testid={`confirm-delete-${user.id}`}
                          >
                            حذف نهائي
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-gray-600 text-gray-400"
                            onClick={() => setConfirmDelete(null)}
                          >
                            إلغاء
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-500 hover:bg-red-600/20"
                          onClick={() => setConfirmDelete(user.id)}
                          data-testid={`delete-btn-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4 ml-1" />
                          حذف نهائي
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* تحذير عند تأكيد الحذف */}
                  {confirmDelete === user.id && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-red-500 font-semibold text-sm">تحذير!</p>
                          <p className="text-gray-400 text-sm">
                            سيتم حذف هذا المستخدم نهائياً ولا يمكن التراجع عن هذا الإجراء.
                            سيتم حذف حساب المستخدم المرتبط به أيضاً.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedUsers;
